<template>
    <div>
        <form class="row align-items-end mb-4" @submit.prevent="doFind">
            <div class="col-md-2 mb-3">
                <label for="filter-year" class="form-label">Event Year</label>
                <select id="filter-year" v-model="dataFilter.eventYear" class="form-control" @change="onYearChange">
                    <option value="">All years</option>
                    <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                </select>
            </div>
            <div class="col-md-3 mb-3">
                <label for="filter-event" class="form-label">Event</label>
                <select id="filter-event" v-model="dataFilter.eventId" class="form-control" :disabled="!dataFilter.eventYear">
                    <option value="">All events</option>
                    <option v-for="item in events" :key="item.value" :value="item.value">{{ item.label }}</option>
                </select>
            </div>
            <div class="col-md-4 mb-3">
                <label for="filter-search" class="form-label">Search</label>
                <InputText id="filter-search" v-model="dataFilter.searchText" class="form-control"
                           placeholder="Title, description, event or session"/>
            </div>
            <div class="col-md-3 mb-3">
                <button type="submit" class="btn btn-primary btn-sm mr-2" :disabled="loading">
                    <i class="pi pi-search mr-1"></i>{{ loading ? 'Loading...' : 'Filter' }}
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearFilters">
                    <i class="pi pi-times mr-1"></i>Clear
                </button>
            </div>
        </form>

        <div v-if="loading" class="text-center text-muted py-5">
            <i class="pi pi-spin pi-spinner mr-2"></i>Loading videos...
        </div>

        <div v-else-if="dataList.length === 0" class="empty-state text-center py-5">
            <i class="pi pi-video empty-icon"></i>
            <p class="mb-3 text-muted">
                {{ hasFilter ? 'No videos match these filters.' : 'No videos have been added yet.' }}
            </p>
            <router-link v-if="!hasFilter" to="/video-gallery/add" class="btn btn-primary btn-sm">
                <i class="pi pi-plus mr-1"></i>Add Videos
            </router-link>
            <button v-else type="button" class="btn btn-outline-secondary btn-sm" @click="clearFilters">Clear filters</button>
        </div>

        <div v-else class="video-grid">
            <article v-for="video in dataList" :key="video.id" class="video-card">
                <button type="button" class="video-thumb" :aria-label="`Play ${video.title}`" @click="play(video)">
                    <img :src="video.thumbnailUrl" :alt="video.title" loading="lazy"/>
                    <span class="play-badge"><i class="pi pi-play"></i></span>
                </button>
                <div class="video-body">
                    <h3 class="video-title" :title="video.title">{{ video.title }}</h3>
                    <p class="video-meta mb-1">
                        {{ video.eventName }} · {{ video.eventDetailName || 'No session' }}
                    </p>
                    <p class="video-meta mb-0">{{ formatDate(video.videoDate) }}</p>
                </div>
                <div class="video-actions">
                    <router-link :to="`/video-gallery/edit/${video.id}`" class="btn btn-outline-primary btn-sm">
                        <i class="pi pi-pencil mr-1"></i>Edit
                    </router-link>
                    <button type="button" class="btn btn-outline-danger btn-sm" :disabled="deletingId === video.id"
                            @click="confirmDelete(video)">
                        <i class="pi pi-trash mr-1"></i>Delete
                    </button>
                </div>
            </article>
        </div>

        <VideoPlayerDialog v-model:visible="playerVisible" :video="playing"/>
        <ConfirmDialog/>
    </div>
</template>

<script>
import {defineComponent} from 'vue'
import moment from 'moment'
import apiService from '@/services/ApiService'
import {eventYearOptions} from '@/utils/eventYears'
import VideoPlayerDialog from './VideoPlayerDialog.vue'

export default defineComponent({
    name: "VideoGalleryList",
    components: {VideoPlayerDialog},
    data() {
        return {
            dataList: [],
            dataFilter: {
                eventYear: '',
                eventId: '',
                searchText: ''
            },
            years: eventYearOptions(),
            events: [],
            loading: false,
            deletingId: null,
            playerVisible: false,
            playing: null
        }
    },
    computed: {
        hasFilter() {
            return !!(this.dataFilter.eventYear || this.dataFilter.eventId || this.dataFilter.searchText);
        }
    },
    methods: {
        doFind() {
            this.loading = true;

            const params = {};
            if (this.dataFilter.eventYear) params.eventYear = this.dataFilter.eventYear;
            if (this.dataFilter.eventId) params.eventId = this.dataFilter.eventId;
            if (this.dataFilter.searchText) params.searchText = this.dataFilter.searchText;

            apiService.get('/VideoGallery', {params})
                .then(response => {
                    this.dataList = response.data || [];
                })
                .catch(error => {
                    this.dataList = [];
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to load videos',
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onYearChange() {
            this.dataFilter.eventId = '';
            this.events = [];
            if (!this.dataFilter.eventYear) {
                return;
            }

            apiService.get(`/VideoGallery/events/${this.dataFilter.eventYear}`)
                .then(response => {
                    this.events = response.data;
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to load events',
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                });
        },
        clearFilters() {
            this.dataFilter = {eventYear: '', eventId: '', searchText: ''};
            this.events = [];
            this.doFind();
        },
        play(video) {
            this.playing = video;
            this.playerVisible = true;
        },
        confirmDelete(video) {
            this.$confirm.require({
                message: `Delete "${video.title}" from the gallery? It will disappear from the mobile app.`,
                header: 'Delete video',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.doDelete(video)
            });
        },
        doDelete(video) {
            this.deletingId = video.id;
            apiService.delete(`/VideoGallery/${video.id}`)
                .then(() => {
                    this.dataList = this.dataList.filter(x => x.id !== video.id);
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: `"${video.title}" has been deleted`,
                        life: 5000
                    });
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to delete video',
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.deletingId = null;
                });
        },
        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '-';
        }
    },
    mounted() {
        this.doFind();
    }
})
</script>

<style scoped>
.video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.25rem;
}

.video-card {
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #e3e6f0;
    border-radius: 6px;
    overflow: hidden;
}

.video-thumb {
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    padding: 0;
    border: 0;
    background: #000;
    cursor: pointer;
}

.video-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.15s ease;
}

.video-thumb:hover img,
.video-thumb:focus-visible img {
    opacity: 0.8;
}

.play-badge {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    margin: -1.5rem 0 0 -1.5rem;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
}

.video-body {
    flex: 1;
    padding: 0.75rem 1rem 0.5rem;
}

.video-title {
    margin: 0 0 0.35rem;
    font-size: 1rem;
    font-weight: 600;
    color: #3a3b45;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.video-meta {
    font-size: 0.85rem;
    color: #6e707e;
}

.video-actions {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 1rem 1rem;
}

.empty-icon {
    font-size: 2.5rem;
    color: #b7b9cc;
    display: block;
    margin-bottom: 0.75rem;
}
</style>
